import * as React from "react"
import Helmet from "react-helmet"
import { useNavigate, useParams } from "react-router-dom"

import { formatDistanceToNow } from "date-fns"

import PageContainer from "@podcastsoundboard/ps-lib/components/PageContainer"

import fetchBlogPost from "../../../../api/blogposts/get"
import AppBar from "../../../../components/AppBar"
import Footer from "../../../../components/Footer"
import FullPageContentContainer from "../../../../components/FullPageContentContainer"
import HeadingContainer from "../../../../components/HeadingContainer"
import contentToReadTimeString from "../../../../helpers/strings/contentToReadTimeString"
import { useAppSelector } from "../../../../redux"
import { BlogPost } from "../../../../types/BlogPost"
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"

export default function PostList() {
  const { blogPostUuid } = useParams()
  const navigate = useNavigate()
  const darkModeEnabled = useAppSelector((state) => state.darkModeEnabled)

  const [errorMessage, setErrorMessage] = React.useState("")
  const [blogPostLoading, setBlogPostLoading] = React.useState(false)
  const [blogPost, setBlogPost] = React.useState<BlogPost | null>(null)

  React.useEffect(() => {
    fetchBlogPost(String(blogPostUuid))
      .then((blogPost) => {
        setBlogPostLoading(false)
        setBlogPost(blogPost)
      })
      .catch((err) => {
        console.error(err)
        setBlogPostLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }, [blogPostUuid])
  return (
    <PageContainer darkModeEnabled={darkModeEnabled} className="min-h-screen">
      <AppBar title="Podcast Soundboard" />

      <Toolbar />

      {blogPost && (
        <Helmet>
          <title>{blogPost.title}</title>
          <meta name="description" content={blogPost.subtitle} />
        </Helmet>
      )}

      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity="error">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <HeadingContainer>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            onClick={(e) => {
              e.preventDefault()
              navigate("/app/blog")
            }}
            underline="hover"
            color="inherit"
            href="/app/blog"
          >
            Guides
          </Link>

          <Typography color="text.primary">Show Post</Typography>
        </Breadcrumbs>
      </HeadingContainer>

      <FullPageContentContainer maxWidth="md">
        {blogPostLoading && (
          <Box className="flex justify-center">
            <CircularProgress />
          </Box>
        )}

        {blogPost && (
          <Paper elevation={0} className="p-6 pt-4 mb-8">
            <Typography className="pb-6 blog-title" variant="h3" component="h1">
              {blogPost.title}
            </Typography>
            <Typography
              className="pb-6 blog-subtitle"
              color="text.secondary"
              variant="h6"
              component="h2"
            >
              Created{" "}
              {formatDistanceToNow(new Date(blogPost.createdAt), {
                addSuffix: true,
              })}{" "}
              | {contentToReadTimeString(blogPost.content)}
            </Typography>

            {blogPost.coverImageUrl && !blogPost.coverImageHidden && (
              <Box className="mb-8">
                <img
                  alt="Cover art"
                  style={{ width: "100%" }}
                  src={blogPost.coverImageUrl}
                />
              </Box>
            )}

            <Box className="pb-6 blog-body">
              <div
                dangerouslySetInnerHTML={{ __html: blogPost.content }}
                className={darkModeEnabled ? "dark-mode-links" : ""}
              />
            </Box>
          </Paper>
        )}
      </FullPageContentContainer>

      <Footer />
    </PageContainer>
  )
}
